import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import ContactSection from "../home/components/ContactSection";
import SEO from "../../components/base/SEO";

const contactChannels = [
  {
    icon: "ri-phone-fill",
    title: "Call or Text Us",
    desc: "Speak directly with our team. Available Mon–Fri 7am–6pm for scheduling, and for emergencies.",
    action: "Call (959) 241-8524",
    href: "tel:9592418524",
    highlight: false,
  },
  {
    icon: "ri-mail-send-line",
    title: "Email Us",
    desc: "Send us a detailed message any time. We respond to all emails within 1–2 business hours during working hours.",
    action: "ultimateheatingandcoolingllc@gmail.com",
    href: "mailto:ultimateheatingandcoolingllc@gmail.com",
    highlight: false,
  },
];

const siteUrl: string = (import.meta.env.VITE_SITE_URL as string) ?? "";

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Us | Ultimate Heating & Cooling | CT HVAC Service",
  "description": "Contact Ultimate Heating & Cooling in Connecticut. Call, live chat, email, or submit a service request form. Fast response guaranteed.",
  "url": `${siteUrl}/contact`,
};

export default function ContactPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us | Ultimate Heating & Cooling | CT HVAC Scheduling"
        description="Contact Ultimate Heating & Cooling in Connecticut — call, live chat, email, or request a free quote online. Serving New London, Tolland & Hartford County CT."
        keywords="contact HVAC Connecticut, schedule HVAC service CT, Ultimate Heating Cooling contact, HVAC free quote Somers CT"
        canonical="/contact"
        jsonLd={contactJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-64 overflow-hidden">
        <img
          src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/3b630a248a7247d6c60d74626db0e49c.jpeg"
          alt="Contact Ultimate Heating & Cooling"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2447]/90 via-[#0B2447]/70 to-[#0B2447]/30"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <span className="inline-block bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded-full mb-4">CONTACT US</span>
            <h1 className="text-4xl font-extrabold text-white leading-tight mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              We&apos;re Here to Help
            </h1>
            <p className="text-white/80 text-sm max-w-md leading-relaxed">
              Reach us any way you prefer — call, email, or fill out the form below. Most requests answered within 1–2 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Reach Us */}
      <section className="py-16 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">REACH US</span>
            <h2 className="text-2xl font-extrabold text-[#0B2447] mt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Two Ways to Get in Touch
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto leading-relaxed">
              Pick whatever&apos;s easiest for you — we&apos;re available across all channels.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contactChannels.map((ch) => (
              <div
                key={ch.title}
                className={`relative rounded-2xl p-7 border transition-all ${
                  ch.highlight
                    ? "bg-[#0B2447] border-[#0B2447] text-white"
                    : "bg-white border-gray-100"
                }`}
              >
                {ch.badge && (
                  <span className="absolute top-4 right-4 bg-[#C8102E] text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide">
                    {ch.badge}
                  </span>
                )}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${
                  ch.highlight ? "bg-[#C8102E]/20" : "bg-[#C8102E]/10"
                }`}>
                  <i className={`${ch.icon} text-2xl text-[#C8102E]`}></i>
                </div>
                <h3 className={`font-bold text-base mb-2 ${ch.highlight ? "text-white" : "text-[#0B2447]"}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {ch.title}
                </h3>
                <p className={`text-sm leading-relaxed mb-5 ${ch.highlight ? "text-gray-300" : "text-gray-500"}`}>
                  {ch.desc}
                </p>
                <a
                  href={ch.href}
                  className={`inline-flex items-center gap-2 text-xs font-bold rounded-full px-4 py-2.5 transition-colors cursor-pointer whitespace-nowrap ${
                    ch.highlight
                      ? "bg-[#C8102E] text-white hover:bg-[#A8091D]"
                      : "bg-[#0B2447]/10 text-[#0B2447] hover:bg-[#0B2447]/20"
                  }`}
                >
                  <i className={`${ch.icon} text-sm`}></i>
                  {ch.action}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section (same as homepage) */}
      <ContactSection />

      <Footer />
    </div>
  );
}
